import { CardHeader, CardTitle, Card, CardContent } from '@exam-prep/ui';
import { untilIst } from '../../../lib/format';
import type { StreakProgress, GmCard } from '../model/types';
import { streakLabel, streakPercent, prizesVisible, prizeLine } from '../model/flow';
import { GM } from '../model/copy';

/** Thin progress bar (SVG width attribute: CSP-safe, no layout shift). */
function ProgressBar({ pct, label }: { pct: number; label: string }) {
  const v = Math.min(100, Math.max(0, pct));
  return (
    <svg
      viewBox="0 0 100 8"
      preserveAspectRatio="none"
      className="h-2 w-full"
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={v}
    >
      <rect width="100" height="8" rx="4" className="fill-border" />
      <rect width={v} height="8" rx="4" fill="#F2A93B" className="brand-bar-svg" />
    </svg>
  );
}

/** "Streak 9/14" with a bar. */
export function StreakTracker({ streak }: { streak: StreakProgress }) {
  const label = streakLabel(streak);
  return (
    <div className="space-y-1.5">
      <p className="flex items-baseline justify-between gap-3 text-sm">
        <span className="font-display text-lg font-bold tabular-nums">{label}</span>
        <span className="text-xs text-muted-foreground">
          {streak.dueToday ? 'Daily Challenge still to do today' : 'Daily Challenge done for today'}
        </span>
      </p>
      <ProgressBar pct={streakPercent(streak)} label={label} />
    </div>
  );
}

const Row = ({ k, v }: { k: string; v: string }) => (
  <div className="flex justify-between gap-3">
    <dt className="text-muted-foreground">{k}</dt>
    <dd className="text-right font-medium">{v}</dd>
  </div>
);

/** The exam's headline facts. Used on the entry screen and in the Hub. */
export function GmFacts({ gm }: { gm: GmCard }) {
  return (
    <dl className="space-y-1.5 text-sm">
      <Row k={GM.starts} v={untilIst(gm.startsAt)} />
      {gm.joinWindowMin > 0 ? <Row k={GM.joinUntil} v={untilIst(gm.joinClosesAt)} /> : null}
      <Row k={GM.length} v={`${String(gm.durationMin)} min`} />
      {gm.questions ? <Row k={GM.questions} v={String(gm.questions)} /> : null}
      {gm.totalMarks ? <Row k={GM.marks} v={String(gm.totalMarks)} /> : null}
      <Row k={GM.resultsAt} v={untilIst(gm.resultsAt)} />
      {prizesVisible(gm) && gm.prizesInr ? <Row k="Prizes" v={prizeLine(gm.prizesInr)} /> : null}
      {gm.sections.length ? (
        <div className="pt-1">
          <dt className="text-muted-foreground">Sections</dt>
          <dd>
            <ul className="mt-1 space-y-0.5">
              {gm.sections.map((x) => (
                <li key={x.key} className="flex justify-between gap-3 text-xs">
                  <span>{x.name}</span>
                  <span className="text-muted-foreground">
                    {String(x.questions)} Qs · {String(x.marks * x.questions)} marks
                    {x.minutes ? ` · ${String(x.minutes)} min` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </dd>
        </div>
      ) : null}
    </dl>
  );
}

/** Title block with the "extra exam, optional" label. */
export function GmHeader({ gm }: { gm: GmCard }) {
  return (
    <CardHeader>
      <p className="text-xs font-semibold tracking-[0.14em] text-muted-foreground uppercase">
        {GM.title} · {gm.examName}
      </p>
      <CardTitle className="text-xl">{gm.title}</CardTitle>
      <p className="offer-note rounded-xl border p-3 text-xs leading-relaxed">{GM.extra}</p>
    </CardHeader>
  );
}

/** A plain titled card. */
export function Panel({
  title,
  children,
  className,
}: {
  title?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Card {...(className ? { className } : {})}>
      {title ? (
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
      ) : null}
      <CardContent className="space-y-3 text-sm">{children}</CardContent>
    </Card>
  );
}

export function OfflineNote({ online }: { online: boolean }) {
  return online ? null : (
    <p
      role="status"
      className="rounded-xl border border-amber-200 bg-amber-100 p-3 text-xs text-amber-900"
    >
      {GM.offline}
    </p>
  );
}
